import { useEffect, useState } from 'react';
import {
  useLinkMtoOfferNomenclatureMutation,
  useListMtoNomenclatureItemsQuery,
} from '@shared/api';
import type { UnmatchedOffer } from '@entities/mto';
import { apiErrorMessage } from '@entities/mto';
import {
  HitSub,
  Muted,
  Notice,
  SearchHit,
  SearchInput,
  SearchResults,
} from '../styles';
import { AddToNomenclature } from './AddToNomenclature';

/** Поиск позиции справочника для ручной привязки. Отдельный инпут на строку:
 *  список непривязанных обычно короткий, а общий селект заставлял бы
 *  переключаться между строкой и полем. */
export function NomenclaturePicker({
  offer,
  runId,
  onError,
}: {
  offer: UnmatchedOffer;
  runId: string;
  onError: (msg: string) => void;
}) {
  // Начальный запрос — по тексту позиции из КП: чаще всего нужное найдётся
  // сразу, и снабженцу не придётся печатать.
  const [q, setQ] = useState(offer.position_name);
  const [debounced, setDebounced] = useState(q);
  const [link, { isLoading }] = useLinkMtoOfferNomenclatureMutation();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useListMtoNomenclatureItemsQuery({
    q: debounced.trim() || undefined,
    limit: 6,
  });

  async function handleLink(nomenclatureId: string) {
    onError('');
    try {
      await link({ id: offer.offer_id, runId, nomenclature_id: nomenclatureId }).unwrap();
    } catch (err) {
      onError(apiErrorMessage(err) ?? 'Не удалось привязать позицию к справочнику.');
      // eslint-disable-next-line no-console
      console.error('linkMtoOfferNomenclature failed', err);
    }
  }

  const hits = data?.items ?? [];

  return (
    <>
      <SearchInput
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Поиск по справочнику НСИ"
        aria-label={`Найти позицию справочника для «${offer.position_name}»`}
      />
      <SearchResults>
        {isFetching && hits.length === 0 && <Muted>Ищем…</Muted>}
        {!isFetching && hits.length === 0 && (
          <Muted>Ничего не нашлось — уточните запрос или добавьте позицию в справочник ниже.</Muted>
        )}
        {hits.map((item) => (
          <SearchHit
            key={item.id}
            type="button"
            disabled={isLoading}
            onClick={() => handleLink(item.id)}
          >
            {item.name}
            <HitSub>
              {item.code ? `код ${item.code}` : 'без кода'}
              {item.unit ? ` · ${item.unit}` : ''}
            </HitSub>
          </SearchHit>
        ))}
      </SearchResults>
      <Notice>Нет в справочнике — добавьте как новую позицию:</Notice>
      <AddToNomenclature
        offerIds={[offer.offer_id]}
        defaultName={offer.position_name}
        defaultUnit={offer.unit}
        runId={runId}
        onError={onError}
      />
    </>
  );
}
